/**
 * Pyrana Tech Radar dataset.
 *
 * Curated, evidence-grounded snapshot of the technologies actually used across
 * the catalogued platform repos (pyrana, pyrana-code-interpreter-tiered,
 * patrick-pyrana-frontend). Every entry maps to
 * real tech found in those repos' manifests/Dockerfiles/CI — this is NOT an
 * aspirational list. Ring placement encodes the build-out brief:
 *   - a single, uniform choice across repos            -> adopt
 *   - real and expanding but not yet a blessed standard -> trial
 *   - two+ solutions for one need, OR no standard set   -> assess (decide w/ peers)
 *   - an older version/approach being phased out        -> sunset
 *
 * When the platform's real stack changes, update entries here and add a new
 * timeline snapshot (with `moved`) to anything that changed rings.
 */
import {
  MovedState,
  RadarEntry,
  RadarQuadrant,
  RadarRing,
  TechRadarLoaderResponse,
} from '@backstage-community/plugin-tech-radar-common';

// As-of date for this radar revision.
const ASOF = new Date('2026-07-10T00:00:00Z');

export const quadrants: RadarQuadrant[] = [
  { id: 'languages', name: 'Languages & Runtimes' },
  { id: 'frameworks', name: 'Frameworks & Libraries' },
  { id: 'platform', name: 'Data & Infrastructure' },
  { id: 'delivery', name: 'Build, Test & Delivery' },
];

export const rings: RadarRing[] = [
  {
    id: 'adopt',
    name: 'Adopt',
    color: '#4FA65B',
    description:
      'The standard, blessed choice — uniform across the platform. Default for new work.',
  },
  {
    id: 'trial',
    name: 'Trial',
    color: '#2E7DBE',
    description:
      'Real and expanding in production, but not yet a platform-wide standard. Build on it with normal care.',
  },
  {
    id: 'assess',
    name: 'Assess',
    color: '#E4A93B',
    description:
      'More than one solution serves this need, or no standard is set yet — converge with the team before committing new work.',
  },
  {
    id: 'sunset',
    name: 'Sunset',
    color: '#C4572E',
    description:
      'An older version or approach being phased out. Do not start new work on it.',
  },
];

type EntryOpts = {
  moved?: MovedState;
  snapshotNote?: string;
  links?: Array<{ url: string; title: string }>;
};

function entry(
  id: string,
  quadrant: string,
  ringId: string,
  title: string,
  description: string,
  opts: EntryOpts = {},
): RadarEntry {
  return {
    key: id,
    id,
    quadrant,
    title,
    description,
    links: opts.links,
    timeline: [
      {
        date: ASOF,
        ringId,
        description: opts.snapshotNote,
        moved: opts.moved ?? MovedState.NoChange,
      },
    ],
  };
}

export const entries: RadarEntry[] = [
  // ── Languages & Runtimes ─────────────────────────────────────────────
  entry(
    'python',
    'languages',
    'adopt',
    'Python 3.11',
    'Standard backend language across every service (pyrana, code-interpreter, patrick it_pm). Pinned to 3.11.',
  ),
  entry(
    'typescript',
    'languages',
    'adopt',
    'TypeScript 5',
    'Standard for all frontends and shared JS libs (^5.6 catalog).',
  ),
  entry(
    'nodejs',
    'languages',
    'assess',
    'Node.js runtime version',
    'Runtimes in use: patrick on 20, pyrana frontend Docker on 24 (while pyrana\'s own .nvmrc/CI still say 20). No agreed version — converge on a single LTS (22 is the middle ground).',
  ),

  // ── Frameworks & Libraries ───────────────────────────────────────────
  entry(
    'nextjs',
    'frameworks',
    'adopt',
    'Next.js 16',
    'Uniform frontend framework — every web surface is on 16.2.6 (pyrana, storybook, patrick), standalone output + turbopack.',
    { links: [{ url: 'https://nextjs.org/docs', title: 'Next.js docs' }] },
  ),
  entry(
    'react',
    'frameworks',
    'adopt',
    'React 19',
    'Uniform UI library across all frontends (^19.2). No divergence worth flagging.',
  ),
  entry(
    'tailwindcss',
    'frameworks',
    'adopt',
    'TailwindCSS 4',
    'Standard styling layer for the frontends. Minor drift: patrick still on tailwind-merge v2 vs v3 elsewhere.',
  ),
  entry(
    'tanstack',
    'frameworks',
    'adopt',
    'TanStack Query & Table',
    'Standard data-fetching/caching (Query 5) and table (Table 8) primitives across the React frontends, alongside Radix UI.',
  ),
  entry(
    'fastapi',
    'frameworks',
    'adopt',
    'FastAPI',
    'Standard Python web framework for every backend/API service, served by Uvicorn/Starlette (+ sse-starlette for streaming).',
  ),
  entry(
    'litellm',
    'frameworks',
    'adopt',
    'LiteLLM (LLM gateway)',
    'The blessed abstraction in front of model providers (pyrana), backed by an Azure OpenAI proxy config. Prefer this over calling provider SDKs directly.',
  ),
  entry(
    'temporal',
    'frameworks',
    'assess',
    'Temporal (workflow engine)',
    'pyrana runs Temporal for durable async work (dedicated temporal/connector/file-extraction worker images).',
    { links: [{ url: 'https://docs.temporal.io', title: 'Temporal docs' }] },
  ),
  entry(
    'mcp-sdk',
    'frameworks',
    'assess',
    'MCP server SDK',
    'fastmcp is the MCP library in use (pyrana, patrick it_pm).',
  ),
  entry(
    'web-auth',
    'frameworks',
    'assess',
    'Web-tier auth',
    'pyrana/patrick frontends use the in-house @pyrana/auth + Azure Entra path.',
  ),
  entry(
    'docling',
    'frameworks',
    'trial',
    'Docling (document extraction)',
    'pyrana\'s file-extraction worker uses Docling (+ CPU torch) for document parsing. Proven but single-surface.',
  ),
  entry(
    'zod4',
    'frameworks',
    'adopt',
    'Zod 4',
    'Standard runtime schema validation for the frontends (^4.3). New work should use v4.',
    { moved: MovedState.Up },
  ),
  entry(
    'zod3',
    'frameworks',
    'sunset',
    'Zod 3',
    'patrick-pyrana-frontend still pins zod ^3.23. Migrate to the v4 line used everywhere else.',
    { moved: MovedState.Down },
  ),
  entry(
    'openai-sdk-v1',
    'frameworks',
    'sunset',
    'OpenAI Python SDK v1',
    'pyrana root/backend-apps still floor openai >=1.78, while pyrana_services (>=2.24) is on v2. Raise the v1 floors to the v2 line.',
    { moved: MovedState.Down },
  ),

  // ── Data & Infrastructure ────────────────────────────────────────────
  entry(
    'postgresql',
    'platform',
    'adopt',
    'PostgreSQL',
    'Standard relational store platform-wide (managed + pg-cron in pyrana). The default system of record.',
  ),
  entry(
    'redis',
    'platform',
    'adopt',
    'Redis',
    'Standard cache / queue backing (6.x pyrana, 7.x patrick).',
  ),
  entry(
    'sqlalchemy',
    'platform',
    'adopt',
    'SQLAlchemy 2.0',
    'Standard ORM / query layer in the Python services.',
  ),
  entry(
    'asyncpg',
    'platform',
    'adopt',
    'asyncpg',
    'The blessed async Postgres driver (present in every backend). New data access should use asyncpg (or psycopg 3), not psycopg2.',
    { moved: MovedState.Up },
  ),
  entry(
    'psycopg2',
    'platform',
    'sunset',
    'psycopg2-binary',
    'Still used in pyrana root/backend. Superseded by asyncpg / psycopg 3 elsewhere — migrate off it.',
    { moved: MovedState.Down },
  ),
  entry(
    'openfga',
    'platform',
    'adopt',
    'OpenFGA (ReBAC)',
    'Standard relationship-based authorization (pyrana), openfga-sdk ~0.10.',
  ),
  entry(
    'azure-blob',
    'platform',
    'adopt',
    'Azure Blob Storage',
    'Standard object store across services (azure-storage-blob 12.x).',
  ),
  entry(
    'k8s-helm',
    'platform',
    'adopt',
    'Kubernetes + Helm (AKS/ACR)',
    'Uniform packaging + deploy: Helm charts to AKS, images to ACR, across every repo.',
  ),
  entry(
    'azure-identity',
    'platform',
    'adopt',
    'Azure (Entra / Workload Identity)',
    'Cloud + identity standard — Entra/MSAL, Workload Identity, and an Azure Artifacts private package feed for @pyrana Python libs.',
  ),
  entry(
    'neo4j',
    'platform',
    'assess',
    'Neo4j 5 (graph DB)',
    'Emerging graph store — appears in patrick it_pm (5.x). A de-facto standard that is not yet ratified: promote to Adopt or formally decide.',
    { links: [{ url: 'https://neo4j.com/docs', title: 'Neo4j docs' }] },
  ),
  entry(
    'firecracker',
    'platform',
    'trial',
    'Firecracker microVMs',
    'code-interpreter isolates tiered code execution in Firecracker microVMs (vmlinux + ext4 rootfs). Specialized but production — the sandbox standard for untrusted code.',
  ),
  entry(
    'duckdb-polars',
    'platform',
    'trial',
    'DuckDB / Polars (in-executor analytics)',
    'The code-interpreter executor ships DuckDB + Polars + pandas/pyarrow for in-sandbox analytics. Scoped to that surface.',
  ),
  entry(
    'opentelemetry',
    'platform',
    'adopt',
    'OpenTelemetry',
    'Standard telemetry across backends. Note: pyrana pins OTel ==1.40.0 exact while others float >=1.20,<2.0 — align the pin.',
  ),
  entry(
    'langfuse',
    'platform',
    'trial',
    'Langfuse (LLM tracing)',
    'LLM observability in pyrana (>=2,<5).',
  ),
  entry(
    'app-insights',
    'platform',
    'sunset',
    'Application Insights (frontend)',
    'patrick-pyrana-frontend is the only surface on Microsoft Application Insights; everything else standardizes on OpenTelemetry. Migrate it to the OTel standard.',
    { moved: MovedState.Down },
  ),

  // ── Build, Test & Delivery ───────────────────────────────────────────
  entry(
    'uv',
    'delivery',
    'adopt',
    'uv (Python packaging)',
    'Uniform Python package/dependency manager, CI-pinned (~0.11.25) across every backend.',
    { links: [{ url: 'https://docs.astral.sh/uv/', title: 'uv docs' }] },
  ),
  entry(
    'pnpm',
    'delivery',
    'adopt',
    'pnpm (JS packaging)',
    'Standard JS package manager + workspaces. v10 in pyrana; patrick trails on 9.15 — upgrade it.',
  ),
  entry(
    'vite',
    'delivery',
    'adopt',
    'Vite',
    'Standard frontend bundler/dev server (^8) for the pyrana app + storybook.',
  ),
  entry(
    'vitest',
    'delivery',
    'adopt',
    'Vitest',
    'Standard JS/TS unit test runner (^4) across the frontends.',
  ),
  entry(
    'playwright',
    'delivery',
    'adopt',
    'Playwright',
    'Standard end-to-end browser testing (pyrana).',
  ),
  entry(
    'pytest',
    'delivery',
    'adopt',
    'pytest',
    'Standard Python test framework (+ pytest-asyncio, pytest-cov, hypothesis) across backends.',
  ),
  entry(
    'ruff',
    'delivery',
    'adopt',
    'Ruff',
    'Standard Python linter/formatter everywhere. Version spread (0.6 → 0.15 across repos) — worth aligning, but the tool itself is settled.',
  ),
  entry(
    'github-actions',
    'delivery',
    'adopt',
    'GitHub Actions',
    'Uniform CI/CD across all four repos.',
  ),
  entry(
    'supply-chain',
    'delivery',
    'adopt',
    'Supply-chain security (Trivy · cosign · CodeQL)',
    'Standard build-security toolchain: Trivy image scanning, cosign/sigstore signing, CodeQL SAST — present across the pipelines.',
  ),
  entry(
    'storybook',
    'delivery',
    'trial',
    'Storybook',
    'Component workshop for the pyrana design system (storybook 10, nextjs-vite). Single-surface but real.',
  ),
  entry(
    'pyrana-libs-consumption',
    'delivery',
    'assess',
    '@pyrana/* shared-lib consumption',
    'Two consumption models: pyrana uses the pnpm workspace (workspace:*); patrick pins published versions (@pyrana/ui 1.16.3, @pyrana/charts 16.0.0…) from the Azure feed. Decide how downstream apps track the shared libs — patrick can silently lag the monorepo.',
  ),
];

export const pyranaRadarResponse: TechRadarLoaderResponse = {
  quadrants,
  rings,
  entries,
};

export { MovedState, ASOF };
